import { FC, ReactElement, useEffect, useState } from 'react';
import { DialogContent, OutlinedInput, Stack } from '@mui/material';
import { z } from 'zod';
import { IntlShape, useIntl } from 'react-intl';
import { ControlledFormField } from '../../../form/ControlledFormField';
import { FormField } from '../../../form/FormField';
import { FormErrors } from '../../../form/FormErrors';
import { CreatableMetricTemplateSelect } from './CreatableMetricTemplateSelect';
import { useTypedForm } from '../../../../../shared/fe/hooks/useForm';
import { useApiQuery } from '../../../query/hooks';
import { graphql } from '../../../gql';

const listMetricTemplateQuery = graphql(`
	query createMetricStepBasics_listMetricTemplate {
		listMetricTemplate {
			...CreatableMetricTemplateSelect_MetricTemplate
			id
			name
			description
		}
	}
`);

const createFormSchema = (intl: IntlShape) =>
	z.object({
		metricName: z.string().min(1, intl.formatMessage({ id: 'form.metric.error.empty-name' })),
		templateId: z
			.string()
			.min(1, intl.formatMessage({ id: 'form.metric.error.empty-template' }))
			.nullable(),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

type SubmitValues = Readonly<{
	metricName: string;
	template:
		| Readonly<{
				id: string;
				name: null;
		  }>
		| Readonly<{
				id: null;
				name: string;
		  }>;
}>;

type Props = Readonly<{
	stepper: ReactElement;
	actions: ReactElement;
	onSubmit: (values: SubmitValues) => void;
}>;

const CreateMetricStepBasics: FC<Props> = ({ stepper, actions, onSubmit }) => {
	const { isLoading, data } = useApiQuery(listMetricTemplateQuery);

	const intl = useIntl();

	const form = useTypedForm({
		schema: createFormSchema(intl),
	});

	const [templateName, setTemplateName] = useState<string>();

	const handleSubmit = ({ metricName, templateId }: FormValues) => {
		if (templateId != null) {
			onSubmit({
				metricName,
				template: {
					id: templateId,
					name: null,
				},
			});
			return;
		}

		if (templateName != null) {
			onSubmit({
				metricName,
				template: {
					id: null,
					name: templateName,
				},
			});
			return;
		}
	};

	const handleCreateMetricTemplate = (templateName: string) => {
		// set name which will be used as select placeholder
		setTemplateName(templateName);

		form.reset({
			templateId: null, // reset select value in order to display placeholder
			metricName: templateName,
		});
	};

	const { templateId } = form.watch();

	useEffect(() => {
		const name = data?.listMetricTemplate.find((metricTemplate) => metricTemplate.id === templateId)?.name;
		if (name != null) {
			form.setValue('metricName', name);
		}
	}, [data, form, templateId]);

	return (
		<form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
			<DialogContent>
				{stepper}

				<Stack spacing={3}>
					<ControlledFormField
						form={form}
						name={'templateId'}
						label={intl.formatMessage({ id: 'form.metric.label.template' })}
						description={intl.formatMessage({ id: 'form.metric.label.template.description' })}
						hint={data?.listMetricTemplate.find((metricTemplate) => metricTemplate.id === templateId)?.description ?? undefined}
						render={({ value, ...props }) => (
							<CreatableMetricTemplateSelect
								{...props}
								loading={isLoading}
								metricTemplates={data?.listMetricTemplate ?? []}
								value={value ?? ''}
								onCreate={handleCreateMetricTemplate}
								placeholder={templateName}
							/>
						)}
					/>

					<FormField
						form={form}
						name={'metricName'}
						label={intl.formatMessage({ id: 'form.metric.label.name' })}
						description={intl.formatMessage({ id: 'form.metric.label.name.description' })}
						render={(props) => <OutlinedInput {...props} />}
					/>

					<FormErrors form={form} />
				</Stack>
			</DialogContent>
			{actions}
		</form>
	);
};

export { CreateMetricStepBasics, type SubmitValues as SubmitValuesCreateMetricStepBasics };
